import { formatArticleDate } from '@archetelos/lib/date';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useRequireRole } from '@/lib/auth/useRequireRole';
import { contentQueries } from '@/lib/queries/content';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/creator/content/')({
  component: ContentListRoute,
});

const STATUS_TABS = [
  ['all', 'All'],
  ['draft', 'Drafts'],
  ['pending_review', 'Pending Review'],
  ['published', 'Published'],
  ['unpublished', 'Unpublished'],
  ['rejected', 'Rejected'],
] as const;

function ContentListRoute() {
  const { loading, user } = useRequireRole(['creator'], { creatorStatus: 'approved' });
  const content = useQuery(contentQueries.listForCreator(user?.id));
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<string>('all');
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    let r = content.data ?? [];
    if (tab !== 'all') r = r.filter((c) => c.status === tab);
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((c) => c.title.toLowerCase().includes(q));
    }
    return r;
  }, [content.data, tab, search]);

  if (loading) return null;

  const onDelete = async (id: string, status: string) => {
    if (status === 'published') {
      toast.error(
        'Published content cannot be deleted by contributors. Contact an editor at editorial@archetelos.stream.',
      );
      return;
    }
    if (!window.confirm('Delete this piece? This cannot be undone.')) return;
    if (status === 'pending_review') {
      // Withdraw returns the piece to drafts ('unpublished' stays reserved for
      // was-published-then-pulled).
      const { error } = await supabase.from('content').update({ status: 'draft' }).eq('id', id);
      if (error) {
        toast.error(error.message);
        return;
      }
      toast.success('Withdrawn to drafts.');
    } else {
      const { error } = await supabase.from('content').delete().eq('id', id);
      if (error) {
        toast.error(error.message);
        return;
      }
      toast.success('Deleted.');
    }
    void queryClient.invalidateQueries({ queryKey: ['content', 'creator', user?.id] });
  };

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl text-ink">My content</h1>
        <div className="flex gap-2">
          <Link
            to="/creator/write"
            className="rounded-md bg-ink px-3 py-1.5 text-sm font-medium text-parchment hover:bg-oxblood"
          >
            Write
          </Link>
          <Link
            to="/creator/upload"
            className="rounded-md border border-ink/20 px-3 py-1.5 text-sm text-ink hover:border-oxblood"
          >
            Upload
          </Link>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-1 border-b border-ink/10">
        {STATUS_TABS.map(([val, lbl]) => (
          <button
            key={val}
            type="button"
            onClick={() => setTab(val)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${
              tab === val
                ? 'border-oxblood text-ink'
                : 'border-transparent text-ink/60 hover:text-ink'
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search your content…"
        className="mt-4 w-full max-w-sm rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
      />

      <ul className="mt-6 divide-y divide-ink/10">
        {rows.length === 0 && (
          <li className="py-12 text-center font-sans text-sm text-ink/50">
            Nothing here yet.{' '}
            <Link to="/creator/write" className="text-oxblood hover:underline">
              Write your first essay
            </Link>
            .
          </li>
        )}
        {rows.map((c) => (
          <li key={c.id} className="py-4">
            <div className="flex items-center gap-4">
              {c.cover_image_url ? (
                <img
                  src={c.cover_image_url}
                  alt=""
                  className="h-14 w-20 shrink-0 rounded object-cover"
                />
              ) : (
                <div className="h-14 w-20 shrink-0 rounded bg-ink/5" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-serif text-base text-ink">{c.title}</p>
                <p className="font-sans text-xs text-ink/50">
                  <span className="uppercase tracking-[0.12em]">{c.type}</span> ·{' '}
                  <span className="uppercase tracking-[0.12em]">{c.status.replace('_', ' ')}</span>{' '}
                  · updated {formatArticleDate(new Date(c.updated_at))}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                <Link
                  to="/creator/content/$contentId/edit"
                  params={{ contentId: c.id }}
                  className={
                    c.status === 'rejected'
                      ? 'rounded-md bg-ink px-2.5 py-1 text-xs font-medium text-parchment hover:bg-oxblood'
                      : 'rounded-md border border-ink/20 px-2.5 py-1 text-xs text-ink hover:border-oxblood'
                  }
                >
                  {c.status === 'rejected' ? 'Edit & resubmit' : 'Edit'}
                </Link>
                <Link
                  to="/creator/content/$contentId/preview"
                  params={{ contentId: c.id }}
                  className="rounded-md border border-ink/20 px-2.5 py-1 text-xs text-ink hover:border-oxblood"
                >
                  Preview
                </Link>
                <button
                  type="button"
                  onClick={() => void onDelete(c.id, c.status)}
                  className="rounded-md border border-ink/20 px-2.5 py-1 text-xs text-ink hover:border-signal hover:text-signal"
                >
                  Delete
                </button>
              </div>
            </div>
            {c.status === 'rejected' && (
              <div className="mt-3 rounded-md border border-oxblood/30 bg-oxblood/5 p-4">
                <p className="font-sans text-xs uppercase tracking-[0.12em] text-oxblood">
                  Editor&apos;s notes
                </p>
                <p className="mt-2 font-sans text-sm text-ink/80 pretty">
                  {c.reviewer_notes?.trim() ? c.reviewer_notes : 'Declined — no notes provided.'}
                </p>
                <p className="mt-2 font-sans text-xs text-ink/60">
                  Edit and resubmit to return this piece to review.
                </p>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
